/**
 * FiasPluginBillingCard — shows and assigns the account that pays for every
 * Fias-owned plugin.
 *
 * A Fias-owned plugin's contributor is the platform account, which can never
 * be charged, and nothing on the platform is platform-paid. So every such
 * plugin's costs — publish-time docs and search embeddings, content packs,
 * asset-library storage and egress — go to ONE real account assigned here
 * (`resolveArchePayer` / `resolvePluginCostPayer` server-side). While none is
 * assigned those charges fail or are skipped, which is why the unassigned
 * state reads as a warning.
 *
 * There is no "clear" control on purpose: the only change an admin needs is to
 * replace the account, and clearing it would stop every Fias plugin charge.
 */

import * as React from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, type FiasPluginBillingAccount } from '@/lib/api';
import {
  useFiasPluginBillingAccount,
  useSetFiasPluginBillingAccount,
} from '@/contributor/hooks/usePlatformOwnedPlugins';

const IDENTIFIER_MAX = 254;

function assignErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'USER_NOT_FOUND')
      return 'No account matches that user ID, username or email.';
    if (error.code === 'INVALID_BILLING_ACCOUNT') return error.message;
  }
  return error instanceof Error ? error.message : 'unknown error';
}

function accountLabel(account: FiasPluginBillingAccount): string {
  return account.username ? `${account.username} (${account.userId})` : (account.userId ?? '');
}

export function FiasPluginBillingCard() {
  const { toast } = useToast();
  const accountQuery = useFiasPluginBillingAccount();
  const assign = useSetFiasPluginBillingAccount();
  const [identifier, setIdentifier] = React.useState('');

  const account = accountQuery.data ?? null;
  const assigned = !!account?.userId;
  const trimmed = identifier.trim();

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!trimmed) return;
    assign.mutate(trimmed, {
      onSuccess: (result) => {
        setIdentifier('');
        toast({
          title: 'Billing account assigned',
          description: accountLabel(result.billingAccount),
        });
      },
    });
  };

  return (
    <Card data-testid="fias-plugin-billing-card">
      <CardHeader>
        <CardTitle>Billing account</CardTitle>
        <CardDescription>
          Every Fias-owned plugin is paid for by this account&apos;s credits: publish-time
          documentation and search embeddings, content packs, and asset-library storage and egress.
          The platform never pays.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {accountQuery.isLoading ? (
          <Skeleton className="h-5 w-64" />
        ) : accountQuery.isError ? (
          <p className="text-sm text-destructive">
            Could not load the billing account:{' '}
            {accountQuery.error instanceof Error ? accountQuery.error.message : 'unknown error'}
          </p>
        ) : assigned && account ? (
          <p className="text-sm">
            <span className="text-muted-foreground">Paid by </span>
            <span className="font-medium" data-testid="fias-plugin-billing-account">
              {accountLabel(account)}
            </span>
          </p>
        ) : (
          <p
            className="rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            role="alert"
          >
            No billing account is assigned. Until one is, Fias plugin charges fail and paid
            publish-time work is skipped.
          </p>
        )}

        <form className="flex max-w-xl flex-col gap-2 sm:flex-row sm:items-end" onSubmit={onSubmit}>
          <div className="flex-1 space-y-1">
            <Label htmlFor="fias-plugin-billing-identifier">
              {assigned ? 'Change to' : 'Assign'}
            </Label>
            <Input
              id="fias-plugin-billing-identifier"
              placeholder="User ID, username or email"
              value={identifier}
              maxLength={IDENTIFIER_MAX}
              onChange={(e) => setIdentifier(e.target.value)}
              disabled={assign.isPending}
            />
          </div>
          <Button type="submit" disabled={!trimmed || assign.isPending}>
            {assign.isPending ? 'Saving…' : assigned ? 'Change account' : 'Assign account'}
          </Button>
        </form>
        {assign.isError && (
          <p className="text-sm text-destructive" role="alert">
            {assignErrorText(assign.error)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
